import { useStreamValue, valueIsNot } from 'annotation-tools/utils';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { fetchTokenAndUserDetail as getAuthToken } from '../api/fetch-functions/user/login';
import { sendLogoutSignal } from '../api/fetch-functions/user/logout';
import { IUser, IUserType } from '../types/record/user';

const tokenSource$ = new BehaviorSubject<string | undefined>(undefined);
const userSource$ = new BehaviorSubject<IUserType>(IUser());

export const token$ = tokenSource$.asObservable().pipe(distinctUntilChanged());
export const loggedIn$ = token$.pipe(
  valueIsNot(undefined),
  distinctUntilChanged()
);
export const user$ = userSource$.asObservable().pipe(distinctUntilChanged());

// CSRFトークンを取得する関数
const fetchCSRFToken = async () => {
  try {
    const response = await fetch('/api/csrf/', {
      method: 'GET',
      credentials: 'include'
    });
    if (response.ok) {
      const data = await response.json();
      return data.csrfToken;
    }
  } catch (error) {
    console.warn('Failed to fetch CSRF token:', error);
  }
  return null;
};

export const login = async ({
  email,
  password
}: Readonly<{ email: string; password: string }>) => {
  // ログイン前にCSRFトークンを必ず再取得（セッションタイムアウト対策）
  await fetchCSRFToken();
  
  const result = await getAuthToken({ email, password });
  if (result.type === 'error') {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    tokenSource$.next(undefined);
    userSource$.next(IUser());
    
    // エラーの場合もCSRFトークンを再取得
    await fetchCSRFToken();
  } else {
    localStorage.setItem('token', result.body.token);
    localStorage.setItem('user', JSON.stringify(result.body.user.toJSON()));
    tokenSource$.next(result.body.token);
    userSource$.next(result.body.user);
    
    // ログイン成功後、新しいセッションでCSRFトークンを取得
    await fetchCSRFToken();
  }
  return result;
};

export const logout = async () => {
  const token = localStorage.getItem('token');
  
  // まずローカル状態をクリア（APIコールの結果に関わらず）
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  tokenSource$.next(undefined);
  userSource$.next(IUser());
  
  if (token) {
    try {
      const result = await sendLogoutSignal(token);
      return result;
    } catch (error) {
      // ログアウトAPIが失敗してもローカル状態はすでにクリアされている
      console.warn('Logout API call failed, but local state cleared', error);
      return { type: 'success' as const, body: {}, headers: new Headers() };
    }
  } else {
    return { type: 'success' as const, body: {}, headers: new Headers() };
  }
};

{
  // onload
  const token = localStorage.getItem('token');
  const user = localStorage.getItem('user');
  tokenSource$.next(token === null ? undefined : token);
  userSource$.next(user === null ? IUser() : IUser(JSON.parse(user)));
  
  // アプリケーション起動時にCSRFトークンを取得（セッション切れの場合も考慮）
  fetchCSRFToken();
}

// hooks

export const useToken = () => useStreamValue(token$);
export const useLoggedIn = () => useStreamValue(loggedIn$);
export const useUserDetail = () => useStreamValue(user$, IUser());
